import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

export const emailProcessingService = {
  /**
   * Trigger Edge Function to process queued automation emails
   */
  async processQueuedEmails() {
    try {
      const { data, error } = await supabase?.functions?.invoke('process-automation-emails', {
        body: {}
      });

      if (error) {
        console.error('Error invoking process-automation-emails function:', error);
        return {
          success: false,
          error: error?.message || 'Failed to process emails'
        };
      }

      return {
        success: true,
        data: data
      };
    } catch (error) {
      console.error('Failed to process queued emails:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error'
      };
    }
  },

  /**
   * Get emails eligible for retry (failed with retry_count < 3)
   */
  async getEmailsForRetry() {
    try {
      const { data, error } = await supabase?.rpc('get_emails_for_retry');

      if (error) {
        console.error('Error fetching emails for retry:', error);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch emails for retry:', error);
      return [];
    }
  },

  /**
   * Manually retry failed email
   */
  async retryFailedEmail(recipientId) {
    try {
      // Update retry count and timestamp
      const { data: recipient, error: fetchError } = await supabase?.from('automation_execution_recipients')?.select('retry_count')?.eq('id', recipientId)?.single();

      if (fetchError) {
        console.error('Error fetching recipient:', fetchError);
        return { success: false, error: fetchError?.message };
      }

      if (recipient?.retry_count >= 3) {
        return {
          success: false,
          error: 'Maximum retry attempts reached (3)'
        };
      }

      // Update retry tracking
      const { error: updateError } = await supabase?.from('automation_execution_recipients')?.update({
          retry_count: (recipient?.retry_count || 0) + 1,
          last_retry_at: new Date()?.toISOString(),
          error_message: null // Clear error to allow reprocessing
        })?.eq('id', recipientId);

      if (updateError) {
        console.error('Error updating recipient for retry:', updateError);
        return { success: false, error: updateError?.message };
      }

      // Trigger processing
      const result = await this.processQueuedEmails();
      return result;
    } catch (error) {
      console.error('Failed to retry email:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error'
      };
    }
  },

  /**
   * Handle bounce notification from Resend webhook
   */
  async handleBounce(resendEmailId, bounceType, errorMessage) {
    try {
      const { error } = await supabase?.rpc('handle_email_bounce', {
        p_resend_email_id: resendEmailId,
        p_bounce_type: bounceType,
        p_error_message: errorMessage
      });

      if (error) {
        console.error('Error handling bounce:', error);
        return { success: false, error: error?.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Failed to handle bounce:', error);
      return {
        success: false,
        error: error?.message || 'Unknown error'
      };
    }
  },

  /**
   * Get delivery statistics
   */
  async getDeliveryStats(startDate, endDate) {
    try {
      const { data, error } = await supabase?.from('client_email_logs')?.select('status')?.gte('sent_at', startDate)?.lte('sent_at', endDate);

      if (error) {
        console.error('Error fetching delivery stats:', error);
        return null;
      }

      const stats = {
        total: data?.length || 0,
        sent: data?.filter(log => log?.status === 'sent')?.length || 0,
        delivered: data?.filter(log => log?.status === 'delivered')?.length || 0,
        failed: data?.filter(log => log?.status === 'failed')?.length || 0,
        bounced: data?.filter(log => log?.status === 'bounced')?.length || 0
      };

      stats.deliveryRate = stats?.total > 0 ? ((stats?.delivered / stats?.total) * 100)?.toFixed(1) : 0;
      stats.bounceRate = stats?.total > 0 ? ((stats?.bounced / stats?.total) * 100)?.toFixed(1) : 0;

      return stats;
    } catch (error) {
      console.error('Failed to fetch delivery stats:', error);
      return null;
    }
  }
};